import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, ChevronDown, X } from 'lucide-react';
import DataDeletionModal from "../Modals/DataDeleteModal";
import ConfirmDeleteModal from "../Modals/ConfirmDeleteModal";
import { notifySuccess, notifyError } from "../helper/Toaster";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { getAllDataDeletionRequest, verifyOTP, approveDataDeletion, denyDataDeletionRequest } from "../../requests";
import styles from "../../_css/SecurityCompliance.module.css";
import ResponseLoader from "../common/ResponseLoader"
import useDebounce, { useDebounceForDataDeletion } from "../hooks/useDebounce";
import Pagination from "../common/Pagination";

const DataDeletion = () => {
    // ... [All your state and hooks remain exactly the same] ...
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState({
        status: '',
        month: ''
    });

    const [searchInput, setSearchInput] = useState('');
    const debouncedSearch = useDebounceForDataDeletion(searchInput, 500);

    // ... [Keep all your existing useEffects, functions, and refs here] ...
    const [showModuleDropdown, setShowModuleDropdown] = useState(false);
    const [showMonthDropdown, setShowMonthDropdown] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [selectedForOtp, setSelectedForOtp] = useState(null);

    const [apiResponse, setApiResponse] = useState({
        data: [],
        pagination: {
            total: 0,
            page: 1,
            pages: 0,
            limit: 10
        }
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const rowsPerPage = 10;
    const dispatch = useDispatch();

    const moduleDropdownRef = useRef(null);
    const monthDropdownRef = useRef(null);

    const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    const fetchDataDeletionRequests = useCallback(async () => {
        setLoading(true);
        setError(null);
        dispatch(startLoader());
        try {
            const params = {
                page,
                limit: rowsPerPage,
                status: filters.status || undefined,
                search: debouncedSearch || undefined
            };

            const response = await getAllDataDeletionRequest(params);
            if (response?.success) {
                setApiResponse({
                    data: response.data || [],
                    pagination: response.pagination || {
                        total: 0,
                        page: 1,
                        pages: 0,
                        limit: rowsPerPage
                    }
                });
            } else {
                throw new Error(response?.message || 'Failed to fetch data');
            }
        } catch (err) {
            setError(err.message);
            notifyError('Failed to load data');
            setApiResponse({
                data: [],
                pagination: {
                    total: 0,
                    page: 1,
                    pages: 0,
                    limit: rowsPerPage
                }
            });
            console.error('Error fetching data deletion requests:', err);
        } finally {
            setLoading(false);
            dispatch(stopLoader());
        }
    }, [page, filters.status, debouncedSearch, dispatch]);

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch]);

    useEffect(() => {
        fetchDataDeletionRequests();
    }, [debouncedSearch, page, filters.status]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (moduleDropdownRef.current && !moduleDropdownRef.current.contains(event.target)) {
                setShowModuleDropdown(false);
            }
            if (monthDropdownRef.current && !monthDropdownRef.current.contains(event.target)) {
                setShowMonthDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const filterOptions = () => {
        const order = ["Denied", "Pending", "Approved"];
        return { status: order, months: monthNames };
    };

    const filteredData = useMemo(() => {
        let data = apiResponse.data;

        if (filters.month) {
            data = data.filter(item => {
                if (!item.timestamp && !item.dateRequested) return false;

                const itemDate = new Date(item.timestamp || item.dateRequested);
                const itemMonth = itemDate.toLocaleDateString('en-US', { month: 'long' });

                return itemMonth === filters.month;
            });
        }

        return [...data].sort((a, b) => {
            const dateA = new Date(a.timestamp || a.dateRequested);
            const dateB = new Date(b.timestamp || b.dateRequested);
            return dateA - dateB;
        });
    }, [apiResponse.data, filters.month]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= apiResponse.pagination.pages) {
            setPage(newPage);
        }
    };

    const handleFilterChange = (filterType, value) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: value
        }));
        setPage(1);
    };

    const handleSearchInputChange = (e) => {
        setSearchInput(e.target.value);
    };

    const toggleModuleDropdown = () => {
        setShowModuleDropdown(!showModuleDropdown);
        setShowMonthDropdown(false);
    };

    const toggleMonthDropdown = () => {
        setShowMonthDropdown(!showMonthDropdown);
        setShowModuleDropdown(false);
    };

    const clearAllFilters = () => {
        setFilters({
            status: '',
            month: ''
        });
        setSearchInput('');
        setPage(1);
        setShowModuleDropdown(false);
        setShowMonthDropdown(false);
    };

    const hasActiveFilters = Object.values(filters).some(filter => filter !== '') || searchInput !== '';

    const displayData = useMemo(() => {
        let data = filters.month ? filteredData : apiResponse.data;

        if (searchInput) {
            const s = searchInput.toLowerCase();
            data = data.filter((row) =>
                row.requestId?.toLowerCase().includes(s) ||
                row.requestedBy?.toLowerCase().includes(s)
            );
        }

        return data;
    }, [apiResponse.data, filteredData, filters.month, searchInput]);

    const openModal = (row) => {
        setSelectedRow(row);
        setShowModal(true);
    }

    const closeModal = () => {
        setSelectedRow(null);
        setShowModal(false);
    }

    const closeOtpModal = () => {
        setSelectedForOtp(null);
        setShowOtpModal(false);
    }

    const handleApprove = (row) => {
        setSelectedForOtp(row);
        setShowOtpModal(true);
    }

    const handleDeny = async (row) => {
        dispatch(startLoader());
        try {
            const response = await denyDataDeletionRequest(row?.id);
            const result = response?.data;
            if (result.success) {
                notifySuccess('Request denied successfully');
                closeModal();
                fetchDataDeletionRequests();
            } else {
                throw new Error(result.message || 'Denial failed');
            }
        } catch (error) {
            console.error('Error denying request:', error);
            notifyError('Failed to deny request');
        } finally {
            dispatch(stopLoader());
        }
    }

    const handleOtpSubmit = async (code) => {
        dispatch(startLoader());
        try {
            const verifyResponse = await verifyOTP(selectedForOtp?.requestId, code);
            if (verifyResponse.success) {
                const approveResponse = await approveDataDeletion(selectedForOtp?.requestId);
                if (approveResponse.success) {
                    notifySuccess("Request approved successfully");
                    closeOtpModal();
                    closeModal();
                    fetchDataDeletionRequests();
                } else {
                    notifyError(approveResponse.message || "Approval failed");
                }
            } else {
                notifyError(verifyResponse.message || "OTP verification failed");
                closeOtpModal();
                closeModal();
            }
        } catch (error) {
            console.error("Error verifying/approving OTP:", error);
            notifyError(error.response?.data?.message || "An error occurred");
            closeOtpModal();
            closeModal();
        } finally {
            dispatch(stopLoader());
        }
    };

    return (
        <div className={styles.container}>
            {/* --- FIX IS HERE: Filter JSX inlined directly --- */}
            <div className={styles.filterContainer}>
                <div className={styles.filterButtons}>
                    {/* Status Dropdown */}
                    <div className={styles.dropdownContainer} ref={moduleDropdownRef}>
                        <button
                            onClick={toggleModuleDropdown}
                            className={styles.filterButton}
                        >
                            {filters.status || "Status"}
                            <ChevronDown
                                size={16}
                                color="#6b7280"
                                style={{ transform: showModuleDropdown ? 'rotate(180deg)' : 'none' }}
                            />
                        </button>
                        {showModuleDropdown && (
                            <div className={styles.dropdownMenu}>
                                <div
                                    className={styles.dropdownItem}
                                    onClick={() => {
                                        handleFilterChange("status", "");
                                        setShowModuleDropdown(false);
                                    }}
                                >
                                    All Status
                                </div>
                                {filterOptions().status.map((status) => (
                                    <div
                                        key={status}
                                        className={styles.dropdownItem}
                                        onClick={() => {
                                            handleFilterChange("status", status);
                                            setShowModuleDropdown(false);
                                        }}
                                    >
                                        {status}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Month Dropdown */}
                    <div className={styles.dropdownContainer} ref={monthDropdownRef}>
                        <button
                            onClick={toggleMonthDropdown}
                            className={styles.filterButton}
                        >
                            {filters.month || "This Month"}
                            <ChevronDown
                                size={16}
                                color="#6b7280"
                                style={{ transform: showMonthDropdown ? 'rotate(180deg)' : 'none' }}
                            />
                        </button>
                        {showMonthDropdown && (
                            <div
                                className={styles.dropdownMenu}
                                style={{
                                    maxHeight: '160px',
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                    scrollbarWidth: 'thin',
                                    scrollbarColor: '#cbd5e0 #f1f5f9'
                                }}
                            >
                                <div
                                    className={styles.dropdownItem}
                                    onClick={() => {
                                        handleFilterChange("month", "");
                                        setShowMonthDropdown(false);
                                    }}
                                >
                                    All Months
                                </div>
                                {filterOptions().months.map((month) => (
                                    <div
                                        key={month}
                                        className={styles.dropdownItem}
                                        onClick={() => {
                                            handleFilterChange("month", month);
                                            setShowMonthDropdown(false);
                                        }}
                                    >
                                        {month}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Search Input */}
                    <div className={styles.searchContainer}>
                        <input
                            type="text"
                            placeholder="Search by email or request ID..."
                            value={searchInput}
                            onChange={handleSearchInputChange}
                            className={styles.searchInput}
                        />
                    </div>

                    {/* Clear Filters Button */}
                    {hasActiveFilters && (
                        <button
                            onClick={clearAllFilters}
                            className={styles.clearFilterButton}
                        >
                            Clear Filters
                            <X size={14} />
                        </button>
                    )}
                </div>
            </div>
            {/* --- End of Filter JSX --- */}

            {/* Loading State */}
            {loading && (
                <div className={styles.loadingState}>
                    <ResponseLoader />
                </div>
            )}

            {/* Table Container */}
            {!loading && !error && (
                <div className="table-container" style={{ borderRadius: '8px', overflow: 'hidden', margin: "20px", }}>
                    <table className={`${styles.table} text-center align-middle`} style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                        {/* Table Header & Body remain the same */}
                        <thead>
                            <tr>
                                <th className={`${styles.tableHeader} ${styles.tableHeaderFirst}`}>
                                    Request ID
                                </th>
                                <th className={styles.tableHeader}>
                                    Requested By
                                </th>
                                <th className={styles.tableHeader}>
                                    Type
                                </th>
                                <th className={styles.tableHeader}>
                                    Date Requested
                                </th>
                                <th className={styles.tableHeader}>
                                    Status
                                </th>
                                <th className={`${styles.tableHeader} ${styles.tableHeaderLast}`}>
                                    Action
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayData.length > 0 ? (
                                displayData.map((row, index) => {
                                    const isLastRow = index === displayData.length - 1;
                                    return (
                                        <tr key={index}>
                                            <td className={`${styles.tableCell} ${styles.tableCellFirst} ${isLastRow ? styles.lastRowFirst : ''}`}>
                                                <span>{row.requestId}</span>
                                            </td>
                                            <td className={`${styles.tableCell} ${isLastRow ? styles.lastRowCell : ''}`}>
                                                {row.requestedBy}
                                            </td>
                                            <td className={`${styles.tableCell} ${isLastRow ? styles.lastRowCell : ''}`}>
                                                {row.type}
                                            </td>
                                            <td className={`${styles.tableCell} ${isLastRow ? styles.lastRowCell : ''}`}>
                                                {new Date(row.dateRequested).toLocaleDateString()}
                                            </td>
                                            <td className={`${styles.tableCell} ${isLastRow ? styles.lastRowCell : ''}`}>
                                                {row.status}
                                            </td>
                                            <td className={`${styles.tableCell} ${styles.tableCellLast} ${isLastRow ? styles.lastRowLast : ''}`}>
                                                <div className="d-flex">
                                                    <span
                                                        role="button"
                                                        className={styles.actionLink}
                                                        onClick={() => openModal(row)}
                                                    >
                                                        {row.status === 'Pending' ? 'View/Approve' : 'View'}
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="6" className={styles.emptyState}>
                                        {hasActiveFilters || searchInput
                                            ? "No records found matching the selected filters"
                                            : "No records found"}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <DataDeletionModal
                show={showModal}
                onHide={closeModal}
                data={selectedRow}
                onApprove={handleApprove}
                onDeny={handleDeny}
            />
            <ConfirmDeleteModal
                show={showOtpModal}
                onHide={closeOtpModal}
                email={selectedForOtp?.requestedBy}
                onSubmit={handleOtpSubmit}
                requestId={selectedForOtp?.requestId}
            />

            {/* Pagination Section */}
            {!loading && !error && (
                <Pagination
                    currentPage={page}
                    totalCount={apiResponse.pagination.total}
                    pageSize={rowsPerPage}
                    onPageChange={page => handlePageChange(page)}
                />
            )}
        </div>
    );
};

export default DataDeletion;